import { capitalizeFirstLetter } from "./strings";

/**
 * Gets a message based on a counter.
 * @param {Object} params - Parameters for the function.
 * @param {Object} params.messages - Messages to be used.
 * @param {string} params.messages.without - Message when the counter is null.
 * @param {string} params.messages.oneItem - Message when the counter is 1.
 * @param {string} [params.messages.moreThanOne=params.messages.oneItem] - Message when the counter is greater than 1. Default is equal to 'oneItem' message.
 * @param {number} [params.count] - Counter to be used to determine the message.
 * @returns {string} - The message corresponding to the counter.
 */
export const getMessageByCount = ({ messages, count }) => {
  const { oneItem, without, moreThanOne = oneItem } = messages;
  switch (true) {
    case !count:
      return without;

    case Number(count) === 1:
      return oneItem;

    case Number(count) > 1:
      return moreThanOne ?? oneItem;

    default:
      return without;
  }
};

/**
 * Gets a short formatted date as 12/01/99.
 * @param {string} date - Date Parameter in an ISO string format as 2020-01-21 15:42:19.
 * @returns {string} - Formatted date.
 */
export const formatToShortDate = (date) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit"
  }).format(new Date(date.replace(" ", "T")));
};

export const formatFullYearDate = (sendDate) => {
  if (sendDate instanceof Date) {
    const month = sendDate.getMonth() + 1;
    const day = sendDate.getDate();
    const year = sendDate.getFullYear();

    return `${month}/${day}/${year}`;
  }

  return null;
};

export const formatSnakeToCamelCase = (object) => {
  const replacePattern = /([-_][a-z])/gi;
  const removeDashUnderscore = /[-_]/g;

  const toCamelCase = (string) => {
    return string.replace(replacePattern, (match) =>
      match.toUpperCase().replace(removeDashUnderscore, "")
    );
  };

  const formatItem = (item) => {
    if (Array.isArray(item)) {
      return item.map((element) => formatItem(element));
    } else if (typeof item === "function" || !(item instanceof Object)) {
      return item;
    }

    const entries = Object.entries(item).map(([key, value]) => {
      const newKey = toCamelCase(key);
      const newValue = formatItem(value);
      return [newKey, newValue];
    });

    return Object.fromEntries(entries);
  };

  return formatItem(object);
};

/**
 * Formats a given phone number into the format (XXX) XXX-XXXX.
 * Removes any non-numeric characters before formatting.
 *
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} The formatted phone number or an empty string if the input is not present.
 *
 * @example
 * numberToPhone("123-456-7890"); // returns "(123) 456-7890"
 * numberToPhone("(987) 654-3210"); // returns "(987) 654-3210"
 * numberToPhone("1-800-123-4567"); // returns "(800) 123-4567"
 */

export const numberToPhone = (phoneNumber) => {
  if (!phoneNumber) return "";

  const number = phoneNumber.replace(/\D/g, "");
  return `(${number.slice(-10, -7)}) ${number.slice(-7, -4)}-${number.slice(
    -4
  )}`;
};

export const mountCustomError = ({ field, message }) => {
  const formattedFieldText = String(field).replace(/_/g, " ");
  return capitalizeFirstLetter(`${formattedFieldText} - ${message}`);
};
